<template>
  <div id="pp-message" style="min-height: 1rem">
    <div v-if="pending" class="h-4 w-3/4 skeleton" />
  </div>
</template>

<script lang="ts" setup>
import type { PayPalMessagesComponentOptions, PayPalScriptOptions } from '@paypal/paypal-js'
import { loadScript } from '@paypal/paypal-js'

const props = defineProps<{
  amount: number
  placement: Exclude<PayPalMessagesComponentOptions['placement'], undefined>
}>()

const { configPayPal } = useFeatureFlags()
const { style, showAmount } = useAppConfig().components.paypal?.payments || {}
const buyerCountry = useCountryCode()
const pending = ref(true)

// Defined a separate PayPal namespace to avoid conflicts with Adyen.
// Adyen removes the PayPal script from the DOM when it destroys PayPal components,
// but it does not clean up PayPal objects and listeners.
// This causes issues when reinitializing PayPal, as it attempts to clean up during script reload,
// which can fail. To address this, we load the SDK twice and assign two PayPal instances
// to different namespaces, ensuring independence and avoiding these unexpected behaviors.
// Alternatively, we could migrate PayPal button handling away from Adyen and manage PayPal integration ourselves.
const PAYPAL_NAMESPACE = 'paypal-paylater'
const CONTAINER_ID = '#pp-message'

const loadPaypal = async () => {
  const { amount, placement } = props

  const paypalConfig: PayPalScriptOptions = {
    clientId: configPayPal?.clientId,
    components: ['messages'],
    // debug: true, // it helps with debugging PayPal errors
    dataNamespace: PAYPAL_NAMESPACE
  }

  const paypalInstance = window[PAYPAL_NAMESPACE] || await loadScript(paypalConfig)
    .catch((err) => {
      console.error('failed to load the PayPal JS SDK script', err)
    })

  paypalInstance?.Messages?.({
    ...(showAmount[placement] ? { amount } : {}),
    buyerCountry,
    placement,
    style
  } as PayPalMessagesComponentOptions).render(CONTAINER_ID)

  pending.value = false
}

watch(() => props.amount, (value) => {
  document.querySelector(CONTAINER_ID)?.setAttribute('data-pp-amount', value.toString())
})

onMounted(() => loadPaypal())
</script>
